import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, Mail, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SendForSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: string;
  contractTitle: string;
  initialSignerName?: string;
  initialSignerEmail?: string;
  onSuccess?: () => void;
}

export function SendForSignatureDialog({
  open,
  onOpenChange,
  contractId,
  contractTitle,
  initialSignerName = "",
  initialSignerEmail = "",
  onSuccess,
}: SendForSignatureDialogProps) {
  const [signerName, setSignerName] = useState(initialSignerName);
  const [signerEmail, setSignerEmail] = useState(initialSignerEmail);
  const [customMessage, setCustomMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!signerEmail || !signerName) {
      toast.error("Please enter signer name and email");
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-contract-signing-link', {
        body: {
          contractId,
          signerEmail,
          signerName,
          message: customMessage || undefined,
        },
      });

      if (error) throw error;

      toast.success(`Contract sent to ${signerEmail}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error sending contract:", error);
      toast.error("Failed to send contract. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            Send for Signature
          </DialogTitle>
          <DialogDescription>
            Send "{contractTitle}" to the signer for their electronic signature.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="signerName" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Signer Name *
            </Label>
            <Input
              id="signerName"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="John Smith"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="signerEmail" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Signer Email *
            </Label>
            <Input
              id="signerEmail"
              type="email"
              value={signerEmail}
              onChange={(e) => setSignerEmail(e.target.value)}
              placeholder="client@company.com"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="customMessage">Personal Message (optional)</Label>
            <Textarea
              id="customMessage"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a personal note to include in the email..."
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">
              This message will appear in the email sent to the signer.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isSending || !signerEmail || !signerName}>
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
